import React, { useEffect, useState } from "react";
import { Button, Input, Modal } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { ALLERGENS } from "constant/beverages";
import { EditCardProductAPI } from "network/menuCards";

// Modifica di un piatto (o del prezzo di un vino) direttamente dalla Struttura della carta.
// "Solo in questo menu" salva la modifica nella carta; "In Food Product" aggiorna il prodotto in tutte le carte.
const EditItemModal = ({ cardId, item, onClose, onSaved }) => {
  const { t } = useTranslation("cards");
  const [form, setForm] = useState(null);
  const [scope, setScope] = useState("local");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price != null ? String(item.price).replace(".", ",") : "",
      allergens: item.allergens || [],
    });
    setScope("local");
  }, [item]);

  if (!item || !form) return null;
  const wine = !!item.is_wine;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async (body, message) => {
    setBusy(true);
    try {
      const res = await EditCardProductAPI(cardId, item.uuid, body);
      onSaved(res?.data);
      successToaster(message);
      onClose();
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    }
    setBusy(false);
  };

  const save = () => {
    const body = { scope, price: form.price };
    if (!wine) Object.assign(body, { name: form.name, description: form.description, allergens: form.allergens });
    send(body, scope === "local" ? t("structure.edit.savedLocal", { name: form.name }) : t("structure.edit.savedAll", { name: form.name }));
  };

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} backdrop={busy ? "static" : true}>
      <div className="modal-header">
        <h5 className="modal-title">{wine ? t("structure.edit.titleWine") : t("structure.edit.title")}</h5>
        <button aria-label={t("close")} className="close" type="button" disabled={busy} onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="modal-body">
        {wine ? (
          <p className="text-muted"><b>{item.name}</b>{item.vintage ? ` ${parseInt(item.vintage, 10)}` : ""} · {t("structure.edit.wineNote")}</p>
        ) : (
          <>
            <label htmlFor="ei-name" className="d-block">{t("structure.edit.name")}</label>
            <Input id="ei-name" value={form.name} maxLength={200} disabled={busy} onChange={(e) => set("name", e.target.value)} />
            <label htmlFor="ei-descr" className="d-block mt-3">{t("structure.edit.description")}</label>
            <Input id="ei-descr" type="textarea" rows={3} value={form.description} maxLength={1000} disabled={busy} onChange={(e) => set("description", e.target.value)} />
          </>
        )}
        <label htmlFor="ei-price" className="d-block mt-3">{t("structure.edit.price")}</label>
        <Input id="ei-price" inputMode="decimal" style={{ maxWidth: 160 }} value={form.price} disabled={busy} onChange={(e) => set("price", e.target.value)} />
        {!wine && (
          <>
            <div className="mt-3 mb-1">{t("structure.edit.allergens")}</div>
            <div className="d-flex flex-wrap" style={{ gap: "4px 14px" }}>
              {ALLERGENS.map((a) => (
                <label key={a.code} className="mb-0" style={{ fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={form.allergens.includes(a.code)}
                    onChange={(e) => set("allergens", e.target.checked ? [...form.allergens, a.code] : form.allergens.filter((x) => x !== a.code))}
                  />{" "}
                  {a.label}
                </label>
              ))}
            </div>
          </>
        )}
        <div className="mt-4 p-3" style={{ border: "1px solid rgba(128,128,128,.3)", borderRadius: 10 }}>
          <label className="d-block mb-2" style={{ fontWeight: 400 }}>
            <input type="radio" name="ei-scope" checked={scope === "local"} disabled={busy} onChange={() => setScope("local")} /> {t("structure.edit.scopeLocal")}
          </label>
          <label className="d-block mb-0" style={{ fontWeight: 400 }}>
            <input type="radio" name="ei-scope" checked={scope === "all"} disabled={busy} onChange={() => setScope("all")} /> {wine ? t("structure.edit.scopeAllWine") : t("structure.edit.scopeAll")}
          </label>
          {item.local && (
            <Button size="sm" color="link" className="px-0 mt-2" disabled={busy} onClick={() => send({ scope: "reset" }, t("structure.edit.resetDone", { name: item.name }))}>
              {wine ? t("structure.edit.resetWine") : t("structure.edit.reset")}
            </Button>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <Button type="button" color="secondary" disabled={busy} onClick={onClose}>{t("cancel")}</Button>
        <Button type="button" color="primary" disabled={busy || (!wine && !form.name.trim())} onClick={save}>
          {busy ? t("structure.edit.saving") : t("structure.edit.save")}
        </Button>
      </div>
    </Modal>
  );
};

export default EditItemModal;
